import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, bulkAddToGroup } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
interface BulkAddToGroupProps {
  open: boolean;
  handleClose: () => void;
  selectedUsers: string[];
  setSelectedUsers: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function BulkAddToGroupDialog({
  open,
  handleClose,
  selectedUsers,
  setSelectedUsers,
  changeLogRoles,
}: BulkAddToGroupProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [groupTarget, setGroupTarget] = useState('');
  const [selectedRole, setSelectedRole] = useState('MEMBER');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending: isAdding } = useMutation({
    mutationFn: () =>
      bulkAddToGroup(
        'User',
        selectedUsers,
        groupTarget,
        selectedRole,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message:
          'The task to add the user(s) to the selected group has started.',
      });
      selectedUsers.forEach((user) => {
        queryClient.invalidateQueries({ queryKey: [UserKeys.Groups, user] });
      });
      setChangeLogMessage('');
      setGroupTarget('');
      setSelectedRole('MEMBER');
      setSelectedUsers([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue adding the selected user(s) to the group. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_grid.bulk_add_to_group, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.name,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.BulkAddUsersToGroup.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const membershipRoles = [
    {
      displayValue: 'Member',
      value: 'MEMBER',
    },
    {
      displayValue: 'Manager',
      value: 'MANAGER',
    },
    {
      displayValue: 'Owner',
      value: 'OWNER',
    },
  ];

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setGroupTarget('');
        setSelectedRole('MEMBER');
      }}
      title="Add to Group"
      dialogContent={
        <>
          <Typography sx={{ mb: 2 }}>
            {selectedUsers.length > 1 ? (
              <>
                Add <strong>{selectedUsers.length} users</strong> to group.
              </>
            ) : (
              <>
                Add <strong>{selectedUsers[0]}</strong> to group.
              </>
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <SharedAutocomplete
                autoFocus
                sources={['groups']}
                autocompleteValue={groupTarget}
                setAutoCompleteValue={setGroupTarget}
                label="Search for a group"
              />
            </Box>
            <TextField
              select
              label="User Roles"
              value={selectedRole}
              sx={{ width: 150 }}
              onChange={(event) => {
                setSelectedRole(event.target.value);
              }}
            >
              {membershipRoles.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {option.displayValue}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isAdding}
          />
        </>
      }
      primaryButton={{
        loading: isAdding,
        action: handleSubmit,
        disabled: isAdding || groupTarget.length === 0,
        title: 'Add to Group',
      }}
    />
  );
}
